import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar, User } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultPosts = [
  {
    id: "1",
    title: "Getting Started with Pig Farming in Nigeria",
    slug: "getting-started-pig-farming",
    excerpt: "Learn the basics of starting a successful pig farming business in Nigeria. From selecting breeds to managing your piggery.",
    content: "Pig farming is one of the most profitable agricultural ventures in Nigeria...",
    author: "AfriGrow Team",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54tu1wv0_pig-photo111.jpg",
    published: true,
    created_at: "2025-12-01T10:00:00Z"
  },
  {
    id: "2",
    title: "Benefits of Fish Farming: Why Aquaculture Matters",
    slug: "benefits-fish-farming",
    excerpt: "Discover the advantages of fish farming and how it contributes to food security in Nigeria.",
    content: "Fish farming, or aquaculture, is rapidly becoming a key component of Nigeria's agricultural sector...",
    author: "AfriGrow Team",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/wnfypbzf_WhatsApp%20Image%202026-04-01%20at%2010.59.50%20AM.jpeg",
    published: true,
    created_at: "2025-11-15T10:00:00Z"
  },
  {
    id: "3",
    title: "Grasscutter Farming: A Lucrative Opportunity",
    slug: "grasscutter-farming-opportunity",
    excerpt: "Explore the growing market for grasscutter meat and how to start your own grasscutter farm.",
    content: "Grasscutter, also known as cane rat, is a highly sought-after delicacy in West Africa...",
    author: "AfriGrow Team",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54lc9buf_image.png",
    published: true,
    created_at: "2025-10-20T10:00:00Z"
  }
];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      const blogData = response.data.length > 0 ? response.data : defaultPosts;
      setPosts(blogData);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      setPosts(defaultPosts);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="pt-20" data-testid="blog-page">
      {/* Hero Section */}
      <section className="section-padding bg-stone-900 text-white" data-testid="blog-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="font-accent text-sm uppercase tracking-widest text-gold mb-6 block">
              Blog & News
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Farm Insights & Updates
            </h1>
            <p className="text-stone-300 text-lg md:text-xl leading-relaxed">
              Stay updated with the latest news from AfriGrow Farm. Learn about farming 
              techniques, industry insights, and tips for agricultural success.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding bg-stone-50" data-testid="blog-posts-section">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-[16/9] bg-stone-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-stone-200 rounded w-1/2" />
                    <div className="h-6 bg-stone-200 rounded w-full" />
                    <div className="h-4 bg-stone-200 rounded w-full" />
                    <div className="h-4 bg-stone-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {posts.length > 0 && (
                <div className="mb-16" data-testid="featured-post">
                  <div className="grid lg:grid-cols-2 gap-8 bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                    <div className="aspect-[16/9] lg:aspect-auto overflow-hidden">
                      <img
                        src={posts[0].image_url}
                        alt={posts[0].title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium mb-4 w-fit">
                        Featured
                      </span>
                      <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 mb-4">
                        {posts[0].title}
                      </h2>
                      <p className="text-stone-600 text-lg mb-6 line-clamp-3">
                        {posts[0].excerpt}
                      </p>
                      <div className="flex items-center gap-6 text-sm text-stone-500 mb-6">
                        <span className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          {posts[0].author}
                        </span>
                        <span className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDate(posts[0].created_at)}
                        </span>
                      </div>
                      <Button 
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full w-fit px-6"
                        data-testid="read-featured-btn"
                      >
                        Read Article <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Other Posts */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.slice(1).map((post) => (
                  <article
                    key={post.id}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group card-hover"
                    data-testid={`blog-post-${post.id}`}
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-stone-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {post.author}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3 line-clamp-2 group-hover:text-green-600 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-stone-600 text-sm mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <Button 
                        variant="ghost"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 p-0 h-auto font-medium"
                        data-testid={`read-btn-${post.id}`}
                      >
                        Read More <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding bg-white" data-testid="newsletter-section">
        <div className="container-custom">
          <div className="bg-stone-900 rounded-2xl p-8 md:p-16 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-stone-400 text-lg max-w-2xl mx-auto mb-8">
              Want to receive farming tips and updates from AfriGrow Farm? 
              Contact us to join our mailing list.
            </p>
            <Button 
              asChild 
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-6 text-lg"
              data-testid="join-newsletter-btn"
            >
              <Link to="/contact">Contact Us to Subscribe</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-green-600" data-testid="blog-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Have Questions About Farming?
          </h2>
          <p className="text-green-100 text-lg max-w-2xl mx-auto mb-10">
            Our team is happy to share knowledge and answer your agricultural questions. 
            Get in touch with us today.
          </p>
          <Button 
            asChild 
            size="lg"
            className="bg-white text-green-700 hover:bg-stone-100 rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="contact-blog-btn"
          >
            <Link to="/contact">
              Ask Us <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
